import { getFieldErrors, isApiError } from '@/services/api';
import type { TopUpValues } from '../schemas/topUp.schema';
import { hasJustificationRequiredCode } from './accessRefusal';

export const DUPLICATE_REFERENCE_MESSAGE = 'This payment reference is already recorded on this agreement';
export const AGREEMENT_NOT_ACTIVE_MESSAGE = 'This agreement is not active, so a payment cannot be recorded.';
export const TOP_UP_FAILED_MESSAGE = 'The payment could not be recorded. Nothing has been saved; try again.';
export const TAKING_DEALER_REFUSED_MESSAGE =
    'This dealer cannot take payments for this agreement. Choose another invoicing dealer.';
export const TOP_UP_JUSTIFICATION_REQUIRED_MESSAGE =
    'Your recorded reason for accessing this Agreement has expired. Record it again, then record the payment.';

export interface TopUpSubmitErrors {
    fieldErrors: Partial<Record<keyof TopUpValues, string>>;
    formError: string | null;
}

const FIELD_NAME_MAP: Record<string, keyof TopUpValues> = {
    amount_gross: 'amountGross',
    payment_reference: 'paymentReference',
};

const validationErrors = (error: { message: string }, source: unknown): TopUpSubmitErrors => {
    const fieldErrors: TopUpSubmitErrors['fieldErrors'] = {};
    let unmapped = false;
    let dealerRefused = false;
    for (const [field, messages] of Object.entries(getFieldErrors(source))) {
        if (field === 'taking_dealer_id') {
            dealerRefused = true;
            continue;
        }
        const formField = Object.hasOwn(FIELD_NAME_MAP, field) ? FIELD_NAME_MAP[field] : undefined;
        if (formField && messages[0]) fieldErrors[formField] = messages[0];
        else unmapped = true;
    }
    if (dealerRefused) return { fieldErrors, formError: TAKING_DEALER_REFUSED_MESSAGE };
    const noFieldMapped = Object.keys(fieldErrors).length === 0;
    return { fieldErrors, formError: unmapped || noFieldMapped ? error.message : null };
};

export function topUpSubmitErrors(error: unknown): TopUpSubmitErrors {
    if (hasJustificationRequiredCode(error)) {
        return { fieldErrors: {}, formError: TOP_UP_JUSTIFICATION_REQUIRED_MESSAGE };
    }
    if (!isApiError(error)) return { fieldErrors: {}, formError: TOP_UP_FAILED_MESSAGE };
    if (error.status === 422) return validationErrors(error, error);
    if (error.status === 409 && error.code === 'duplicate_payment_reference') {
        return { fieldErrors: { paymentReference: DUPLICATE_REFERENCE_MESSAGE }, formError: null };
    }
    if (error.status === 409 && error.code === 'agreement_not_active') {
        return { fieldErrors: {}, formError: AGREEMENT_NOT_ACTIVE_MESSAGE };
    }
    return { fieldErrors: {}, formError: TOP_UP_FAILED_MESSAGE };
}
